// This used to be loaded from Cephalopod. If this is removed most images will not load.
import "lazysizes";
import "lazysizes/plugins/attrchange/ls.attrchange";
import "../styles.css";
import "../styles/chat.css";
import "../styles/core.css";

import { useEffect } from "react";
import { useClientSideSplitTreatments } from "@leafly-com/split-next";
import { gtmHeadTag } from "@leafly-com/web-utils";
import { getCookie } from "cookies-next";
import App, { AppProps } from "next/app";
import dynamic from "next/dynamic";
import Head from "next/head";
import { useRouter } from "next/router";
import Script from "next/script";
import { Provider, useDispatch, useSelector, useStore } from "react-redux";
import addressTokenMiddleware from "server/middleware/addressTokenMiddleware";

import { getCurrentSitewideBanner } from "api/requests/directus/getCurrentSitewideBanner";
import { getFooterContent } from "api/requests/directus/getFooterContent";
import { getUserLocationData } from "api/requests/getUserLocation";
import { SnackBarProvider } from "context/SnackBarContext";
import { ToastFrame } from "context/Toast/ToastFrame";
import { useDeprecatedLocationFunctions } from "hooks/useDeprecatedLocationFunctions";
import { useIFrameAccessibilityFix } from "hooks/useIFrameAccessibilityFix";
import datadogInit from "lib/datadogInit";
import { createCheckAgeGate } from "redux/action-creators/ageGate";
import { setDomainCountryCode } from "redux/action-creators/config";
import { createLoadCookiesAction } from "redux/action-creators/cookies";
import { loadUser } from "redux/action-creators/user";
import {
  LOAD_FOOTER_CONTENT,
  LoadFooterContentAction,
} from "redux/reducers/cms";
import { LOAD_REQUEST_CONTEXT } from "redux/reducers/config";
import {
  DISABLE_LOADING_SPINNER,
  LOAD_SITEWIDE_BANNER,
  LoadSitewideBanner,
} from "redux/reducers/header";
import { LOCATION_GEO_LOAD, LocationLoadAction } from "redux/reducers/location";
import { RESERVATION_CREATE_CART_ID } from "redux/reducers/reservation";
import { RootState } from "redux/reducers/rootReducer";
import { USER_LOGGED_OUT_REDIRECT } from "redux/reducers/user";
import { getDomainCountryCode } from "redux/selectors/config";
import {
  getHasLoadedLocationCookie,
  getLocationState,
} from "redux/selectors/location";
import { getIsLoggedIn } from "redux/selectors/user";
import { wrapper } from "redux/store";
import { getSplitKey } from "utils/split/getSplitKey";

import AgeGate from "components/AgeGate/AgeGate";
import { ModalFrame } from "components/botanic/Modal/ModalFrame";
import { WeedfettiFrame } from "components/botanic/Weedfetti";
import Footer from "components/Footer/Footer";
import { GlobalLoadingSpinner } from "components/GlobalLoadingSpinner/GlobalLoadingSpinner";
import { GooglePublisherTagScript } from "components/GooglePublisherTagScript";
import { Header } from "components/Header";

import { useGoogleAnalyticsUserLocation } from "../hooks/useGoogleAnalyticsUserLocation";

const ChatwootWidget = dynamic(() => import("components/ChatwootWidget"), {
  ssr: false,
});

/**
 * This component encapsulates side-effects that the app requires in a leaf
 * node, in order to prevent unnecessarily re-rendering any of the application
 * when the dependent state changes. If this code was in the App component
 * itself, changes to the redux state would trigger a re-render of anything
 * rendered by App.
 */
const AppSideEffects = () => {
  const countryCode = useSelector(getDomainCountryCode);
  const dispatch = useDispatch();
  const store = useStore<RootState>();
  const { asPath, query } = useRouter();
  const isLoggedIn = useSelector(getIsLoggedIn);

  useDeprecatedLocationFunctions();
  useIFrameAccessibilityFix();

  useEffect(() => {
    dispatch(createLoadCookiesAction());
    dispatch(createCheckAgeGate(store.getState()));
    dispatch({
      countryCode,
      type: RESERVATION_CREATE_CART_ID,
    });
  }, []);

  useEffect(() => {
    if (!isLoggedIn) {
      return;
    }

    loadUser(countryCode, dispatch);
  }, [isLoggedIn]);

  useEffect(() => {
    datadogInit();
  }, []);

  useEffect(() => {
    if ("sign_out_redirect" in query) {
      dispatch({ isLoggedOutRedirect: true, type: USER_LOGGED_OUT_REDIRECT });
    }
    dispatch({
      type: DISABLE_LOADING_SPINNER,
    });
  }, [asPath]);

  const router = useRouter();

  useGoogleAnalyticsUserLocation();

  typeof window !== "undefined" &&
    router.beforePopState((state) => {
      // This helps retain the scroll position when you hit back. Important for search.
      state.options.scroll = false;
      return true;
    });

  return null;
};

const LeaflyApp = ({
  Component,
  ...rest
}: AppProps<{
  currentPath?: string;
  disableFixedHeader?: boolean;
  disableHeaderCollapseOnScroll?: boolean;
  hideFooter?: boolean;
  hideHeader?: boolean;
  isAppBannerCookieDismissed?: boolean;
  showMinimalHeader?: boolean;
}>) => {
  const {
    store,
    props: { pageProps },
  } = wrapper.useWrappedStore(rest);

  const {
    currentPath,
    disableFixedHeader,
    disableHeaderCollapseOnScroll,
    hideFooter,
    hideHeader,
    isAppBannerCookieDismissed,
    showMinimalHeader,
    ...remainingPageProps
  } = pageProps;

  const { isLoading, treatments } = useClientSideSplitTreatments(
    ["chatwoot_flag"],
    getSplitKey()
  );

  return (
    <Provider store={store}>
      <WeedfettiFrame>
        {!showMinimalHeader && (
          <Head>
            <link
              rel="apple-touch-icon"
              sizes="180x180"
              href="https://public.leafly.com/favicon/apple-touch-icon.png"
            />
            <link
              rel="icon"
              type="image/png"
              sizes="32x32"
              href="https://public.leafly.com/favicon/favicon-32x32.png"
            />
            <link
              rel="icon"
              type="image/png"
              sizes="16x16"
              href="https://public.leafly.com/favicon/favicon-16x16.png"
            />
            <link
              rel="mask-icon"
              href="https://public.leafly.com/favicon/safari-pinned-tab.svg"
              color="#034638"
            />
            <link
              rel="shortcut icon"
              href="https://public.leafly.com/favicon/favicon.ico"
            />
            <meta name="apple-mobile-web-app-title" content="Leafly" />
            <meta name="application-name" content="Leafly" />
            <meta name="theme-color" content="#034638" />
            <meta property="og:site_name" content="Leafly" key="og:site_name" />
            <meta
              property="fb:admins"
              content="210495295724612"
              key="fb:admins"
            />
            <meta
              key="twitter:card"
              name="twitter:card"
              content={currentPath === "/" ? "app" : "summary"}
            />
            <meta name="twitter:site" content="@Leafly" key="twitter:site" />
            <meta property="og:type" content="website" key="og:type" />
          </Head>
        )}
        <Script
          async
          id="google-tag-manager"
          strategy="afterInteractive"
          dangerouslySetInnerHTML={{ __html: gtmHeadTag }}
        />
        <GooglePublisherTagScript />
        {!hideHeader && (
          <Header
            disableFixedPosition={disableFixedHeader}
            disableScrollCollapse={disableHeaderCollapseOnScroll}
            isAppBannerCookieDismissed={isAppBannerCookieDismissed}
            minimal={showMinimalHeader}
          />
        )}
        <AgeGate />
        <div>
          <AppSideEffects />
          <GlobalLoadingSpinner />

          <main>
            <ModalFrame>
              <ToastFrame>
                <SnackBarProvider>
                  <Component {...remainingPageProps} />
                </SnackBarProvider>
              </ToastFrame>
            </ModalFrame>
          </main>
          {!hideFooter && <Footer />}
        </div>

        {!isLoading && treatments.chatwoot_flag.treatment === "on" && (
          <ChatwootWidget />
        )}
      </WeedfettiFrame>
    </Provider>
  );
};

LeaflyApp.getInitialProps = wrapper.getInitialAppProps(
  (store) => async (props) => {
    const { ctx } = props;

    if (ctx.req && ctx.res) {
      /**
       * Changing X-Powered-By is listed as best practices in the express.js
       * security best-practices documentation.
       *
       * See: https://expressjs.com/en/advanced/best-practice-security.html#at-a-minimum-disable-x-powered-by-header
       *
       * Next lets us turn this off completely in config, but that's not very
       * much fun.
       */
      ctx.res.setHeader("X-Powered-By", "Bongs");

      if (await addressTokenMiddleware(ctx.req, ctx.res)) {
        /**
         * This should never result in the page actually being rendered, as the
         * above function will issue a redirect when necessary.
         */
        return { pageProps: {} };
      }

      store.dispatch({
        context: ctx,
        type: LOAD_REQUEST_CONTEXT,
      });
      store.dispatch(setDomainCountryCode(ctx));

      const footerContentRequest = getFooterContent();
      const countryCode = getDomainCountryCode(store.getState());
      const hasLoadedLocationFromCookie = getHasLoadedLocationCookie(
        store.getState()
      );

      if (!hasLoadedLocationFromCookie) {
        const userLocationData = await getUserLocationData(countryCode, ctx);
        store.dispatch<LocationLoadAction>({
          location: userLocationData,
          type: LOCATION_GEO_LOAD,
        });
      }

      const sitewideBanner = await getCurrentSitewideBanner(
        countryCode,
        ctx.req.url as string,
        getLocationState(store.getState())
      );
      store.dispatch<LoadSitewideBanner>({
        banner: sitewideBanner,
        type: LOAD_SITEWIDE_BANNER,
      });

      store.dispatch<LoadFooterContentAction>({
        countryCode,
        footerContent: (await footerContentRequest) || null,
        type: LOAD_FOOTER_CONTENT,
      });
    }

    // If passing either hideFooter or hideHeader as a prop on individual
    // pages, the passed prop will take precedence over these
    const hideHeader = ctx.query["hide_header"] === "true";
    const hideFooter = ctx.query["hide_footer"] === "true";

    const isAppBannerCookieDismissed = Boolean(
      getCookie("appBannerDismissed", ctx)
    );

    return {
      pageProps: {
        currentPath: ctx.asPath,
        hideFooter,
        hideHeader,
        isAppBannerCookieDismissed: isAppBannerCookieDismissed,
        ...(await App.getInitialProps(props)).pageProps,
      },
    };
  }
);

export default LeaflyApp;
